import * as signalR from '@microsoft/signalr';
import { BrowserSpeechRecognition } from './browserSpeechRecognition';
import { getSsoToken } from './ssoTokenProvider';
import {
  getSignalRErrorMessage,
  logSignalR,
  logSignalRError,
} from '../utils/signalRDiagnostics';

export interface TranscriptionSignalREvents {
  onPartial?: (data: { transactionId: string; text: string; speakerId?: string | null }) => void;
  onFinal?: (data: { transactionId: string; text: string; speakerId?: string | null }) => void;
  onStatus?: (data: { transactionId: string; status: string; message?: string }) => void;
  onError?: (data: { transactionId: string; errorCode: string; errorMessage: string }) => void;
  /** The hub connection closed for good (automatic reconnects exhausted or explicit stop). */
  onConnectionClosed?: () => void;
}

export interface MicSessionHandle {
  transactionId: string;
  correlationId: string;
}

/** Recognition settings the hub resolves for a browser mic session (agent Speech config included). */
interface BrowserMicSessionStart extends MicSessionHandle {
  language: string;
  diarization?: boolean;
  diarizeIntermediateResults?: boolean;
  maxSpeakers?: number | null;
  phrases?: string[];
  phraseListWeight?: number | null;
}

/**
 * Client for the transcription hub: live microphone sessions and realtime-file progress.
 *
 * Live mic recognition runs in the browser with the Azure Speech JS SDK (authenticated by a
 * short-lived token from the API — the Speech key never reaches the browser). The hub only
 * receives recognized text: `StartBrowserMicSession` creates the transaction, `ReportFinal`
 * persists finalized utterances, and `StopMicSession` completes the transaction and triggers
 * agent correction. No audio is streamed to the server.
 */
class TranscriptionSignalRService {
  private connection: signalR.HubConnection | null = null;
  private events: TranscriptionSignalREvents = {};
  private recognition: BrowserSpeechRecognition | null = null;
  /** Retained for hub invokes — Azure SignalR hub invokes do not keep negotiate query params. */
  private tenantId: string | null = null;
  private projectId: string | null = null;
  /** Fallback bearer token from the last connect; SSO refresh is attempted first on reconnects. */
  private fallbackToken: string | null = null;
  /**
   * The transaction of the running mic session. Kept so a Stop after a silent reconnect (new
   * connection id — the hub no longer knows the caller) can still resolve the session by id.
   */
  private activeTransactionId: string | null = null;

  /** Registers event callbacks. Safe to call before or after {@link connect}. */
  setEvents(events: TranscriptionSignalREvents): void {
    this.events = events;
    if (this.connection) {
      this.bindHandlers();
    }
  }

  /**
   * Connects to the transcription hub.
   *
   * Tenant/project are passed as hub method arguments (required with Azure SignalR — hub invokes
   * do not keep the negotiate query string). Query params on the hub URL remain for
   * negotiate-time middleware only.
   */
  async connect(baseUrl: string, token: string, tenantId: string, projectId?: string): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      // Reuse the connection only when it was made for the same tenant/project. Hub invokes
      // carry these ids as arguments, so a stale pair would persist sessions under the wrong
      // project after the user switches context in the header.
      if (this.tenantId === tenantId && this.projectId === (projectId ?? null)) {
        return;
      }

      logSignalR('transcription', 'Context changed, reconnecting', {
        previousTenantId: this.tenantId,
        previousProjectId: this.projectId,
        tenantId,
        projectId: projectId ?? null,
      });
      await this.disconnect();
    }

    this.tenantId = tenantId;
    this.projectId = projectId ?? null;
    this.fallbackToken = token;

    const url = new URL(`${baseUrl}/hubs/transcription`);
    url.searchParams.set('tenantId', tenantId);
    if (projectId) {
      url.searchParams.set('projectId', projectId);
    }
    const hubUrl = url.toString();

    logSignalR('transcription', 'Connecting', {
      hubUrl,
      transport: 'WebSockets',
      tenantId: tenantId ?? null,
      projectId: projectId ?? null,
    });

    try {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(hubUrl, {
          // Resolved per (re)connect: a token string captured at connect time can be expired by
          // the time an automatic reconnect negotiates, which fails auth and kills mic sessions.
          accessTokenFactory: () => this.resolveAccessToken(),
          transport: signalR.HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // The server pings every 2.5 min (KeepAliveInterval in Program.cs); the SignalR default of
      // 30s would declare the connection dead between pings and kill live mic sessions.
      this.connection.serverTimeoutInMilliseconds = 6 * 60 * 1000;

      this.bindHandlers();
      this.bindLifecycleHandlers();
      await this.connection.start();

      logSignalR('transcription', 'Connected', {
        hubUrl,
        transport: 'WebSockets',
        connectionId: this.connection.connectionId,
        state: this.connection.state,
      });
    } catch (error) {
      logSignalRError('transcription', 'Connect failed', error, {
        hubUrl,
        transport: 'WebSockets',
        tenantId: tenantId ?? null,
        projectId: projectId ?? null,
        message: getSignalRErrorMessage(error),
        hint: 'WebSockets-only. With Azure SignalR, negotiate stays on the API and WS should go to *.service.signalr.net. Check Azure:SignalR:ConnectionString, SignalR App Server RBAC on the API MI, CORS, and auth token.',
      });
      throw error;
    }
  }

  /** Disconnects, stopping any running microphone recognition first. */
  async disconnect(): Promise<void> {
    await this.teardownRecognition();
    this.activeTransactionId = null;

    if (this.connection) {
      try {
        await this.connection.stop();
      } finally {
        this.connection = null;
      }
    }

    this.tenantId = null;
    this.projectId = null;
    this.fallbackToken = null;
  }

  /** Subscribes to progress for a transaction created through the REST API. */
  async watchTransaction(transactionId: string): Promise<void> {
    await this.requireConnection().invoke('WatchTransaction', transactionId, this.tenantId, this.projectId);
  }

  /**
   * Starts a live microphone session: the hub creates the transaction (applying the agent's
   * Speech config when a slug is given), then the browser recognizes the microphone with the JS
   * Speech SDK and reports finalized utterances back for persistence and agent correction.
   */
  async startMicSession(language: string, clientCorrelationId?: string, agentSlug?: string): Promise<MicSessionHandle> {
    const connection = this.requireConnection();

    const session = (await connection.invoke(
      'StartBrowserMicSession',
      language || null,
      clientCorrelationId || null,
      this.tenantId,
      this.projectId,
      agentSlug || null,
    )) as BrowserMicSessionStart;

    const recognition = new BrowserSpeechRecognition();
    const { transactionId } = session;

    try {
      await recognition.start(
        {
          language: session.language || language || 'en-US',
          diarizeIntermediateResults: session.diarizeIntermediateResults ?? true,
          phrases: session.phrases,
        },
        {
          onPartial: (text, speakerId) => {
            this.events.onPartial?.({ transactionId, text, speakerId });
          },
          onFinal: (text, speakerId) => {
            this.events.onFinal?.({ transactionId, text, speakerId });

            // Persist server-side; the WebSocket delivers messages in order, so finals sent here
            // always land before a later StopMicSession invoke. A transient failure must not stop
            // recognition — the next final carries on and checkpoints cover the gap.
            if (connection.state === signalR.HubConnectionState.Connected) {
              connection.send('ReportFinal', text, speakerId ?? null).catch(() => undefined);
            }
          },
          onError: (message) => {
            this.events.onError?.({ transactionId, errorCode: 'speech_recognition_error', errorMessage: message });
          },
        },
      );
    } catch (error) {
      // Recognition never started (mic denied, token/service unreachable): close the server-side
      // session so the transaction does not linger in Processing.
      await recognition.stop().catch(() => undefined);
      if (connection.state === signalR.HubConnectionState.Connected) {
        await connection.invoke('CancelMicSession').catch(() => undefined);
      }
      throw error;
    }

    this.recognition = recognition;
    this.activeTransactionId = session.transactionId;
    return { transactionId: session.transactionId, correlationId: session.correlationId };
  }

  /**
   * Stops recognition and finalizes the session. Only ids and the terminal status come back over
   * the hub — an hour-long transcript exceeds SignalR message buffers — so fetch the persisted
   * text with GET /transcriptions/{id}. Agent correction runs server-side in the background; poll
   * the same endpoint for correctedText.
   */
  async stopMicSession(): Promise<{
    transactionId: string;
    correlationId: string;
    status: string;
  }> {
    // Stop the recognizer first so the trailing utterance flushes through ReportFinal before the
    // hub completes and persists the transaction.
    await this.teardownRecognition();

    // The transaction id lets the hub resolve the session even when a silent reconnect changed
    // the connection id (or an app recycle dropped its memory); Stop is idempotent server-side.
    const transactionId = this.activeTransactionId;
    this.activeTransactionId = null;
    return (await this.requireConnection().invoke(
      'StopMicSession',
      transactionId,
      this.tenantId,
      this.projectId,
    )) as {
      transactionId: string;
      correlationId: string;
      status: string;
    };
  }

  /** Cancels the session, keeping whatever was recognized so far. */
  async cancelMicSession(): Promise<void> {
    await this.teardownRecognition();
    this.activeTransactionId = null;
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      await this.connection.invoke('CancelMicSession');
    }
  }

  private requireConnection(): signalR.HubConnection {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('Not connected to the transcription hub.');
    }
    return this.connection;
  }

  /**
   * Resolves the bearer token for a (re)connect negotiate: a fresh SSO token when available,
   * otherwise the token supplied at connect time.
   */
  private async resolveAccessToken(): Promise<string> {
    // || (not ??): getSsoToken() can resolve to an empty string when MSAL has no access token.
    const ssoToken = await getSsoToken().catch(() => null);
    return ssoToken || this.fallbackToken || '';
  }

  /**
   * Keeps the mic session and the UI honest across connection-state changes: a reconnect gets a
   * new connection id, so the server-side session must be rebound before ReportFinal/Stop work
   * again, and a closed connection means recognition results can no longer be persisted.
   */
  private bindLifecycleHandlers(): void {
    if (!this.connection) return;

    this.connection.onreconnected(async (connectionId) => {
      logSignalR('transcription', 'Reconnected', { connectionId });

      if (!this.recognition || !this.activeTransactionId) {
        return;
      }

      const transactionId = this.activeTransactionId;
      try {
        // The hub parked the session when the old connection dropped; rebind it to this one so
        // finals keep persisting. Recognition never stopped — it runs in the browser.
        await this.connection!.invoke('ResumeBrowserMicSession', transactionId, this.tenantId, this.projectId);
        logSignalR('transcription', 'Mic session resumed after reconnect', { transactionId });
      } catch (error) {
        logSignalRError('transcription', 'Failed to resume mic session after reconnect', error, {
          transactionId,
        });
        await this.teardownRecognition();
        this.activeTransactionId = null;
        this.events.onError?.({
          transactionId,
          errorCode: 'mic_session_lost',
          errorMessage:
            'The connection dropped and the microphone session could not be resumed. ' +
            'The transcript recognized so far was saved.',
        });
      }
    });

    this.connection.onclose((error) => {
      logSignalR('transcription', 'Connection closed', {
        error: error ? getSignalRErrorMessage(error) : null,
      });

      // Without a hub, recognized text can no longer be reported; stop listening so the UI does
      // not keep showing a live session that persists nothing.
      if (this.recognition) {
        const transactionId = this.activeTransactionId ?? '';
        void this.teardownRecognition();
        this.events.onError?.({
          transactionId,
          errorCode: 'connection_closed',
          errorMessage: 'The connection to the transcription hub was lost, so listening stopped. The transcript recognized so far was saved.',
        });
      }

      this.events.onConnectionClosed?.();
    });
  }

  private bindHandlers(): void {
    if (!this.connection) return;

    this.connection.off('PartialTranscript');
    this.connection.off('FinalTranscript');
    this.connection.off('Status');
    this.connection.off('Error');

    this.connection.on('PartialTranscript', (data) => this.events.onPartial?.(data));
    this.connection.on('FinalTranscript', (data) => this.events.onFinal?.(data));
    this.connection.on('Status', (data) => this.events.onStatus?.(data));
    this.connection.on('Error', (data) => this.events.onError?.(data));
  }

  private async teardownRecognition(): Promise<void> {
    const recognition = this.recognition;
    this.recognition = null;
    if (recognition) {
      await recognition.stop().catch(() => undefined);
    }
  }
}

export const transcriptionSignalRService = new TranscriptionSignalRService();
